import PmiTitle from 'shell/components/shared/title';
import * as yup from 'yup';
import { BodyNotificationStateVM } from 'practice/view-models/practice-detail';
import { DateTime } from 'luxon';
import { Card, CardActions, CardContent, Grid } from '@mui/material';
import AccordionNotificationState from 'practice/components/notification-management/detail/accordion-notification-state';
import NotificationStateList from 'practice/components/notification-management/search/notification-state-list';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { noop } from 'shell/utils/noop';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import TypeCommunicationList from '../search/type-communication-list';
import TypeTransactionList from '../search/type-transaction-list';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { useCallback, useMemo } from 'react';
import PmiDivider from 'shell/components/shared/divider';
import { PmiButton } from 'shell/components/shared/button';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PDND_API_URL__ } from 'shell/constants/environment';
import { PDNDNotificationDetail } from 'shell/models/pdnd-notification';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import TipoDomicilioDigitaleList from './type-transaction-list';
import { regexEmail } from 'shell/constants/regex';

interface NotificationManagementDetailProps {
  stateItems: BodyNotificationStateVM[];
  formData: NotificationManagementFormVM;
  onExit: CallableFunction;
  ultimoScarico: DateTime | null;
  id?: string;
}

interface RecipientVM {
  denomination: string;
  digitalDomicile: string;
  typedigitalDomicile: string;
  recipientType: string;
  taxId: string;
  at: string;
  address: string;
  addressDetails: string;
  zip: string;
  municipality: string;
  municipalityDetails: string;
  province: string;
  foreignState: string;
  payment: string;
}

export interface NotificationManagementFormVM {
  practiceId: string;
  communicationId: string;
  communicationType: string;
  transmissionType: string;
  trasmissionDate: DateTime | null;
  state: string;
  stateDate: DateTime | null;
  stateItems?: BodyNotificationStateVM[];
  recipient?: RecipientVM;
  item?: PDNDNotificationDetail;
}

const NotificationManagementDetailForm = ({
  stateItems,
  formData,
  onExit,
  ultimoScarico,
  id,
}: NotificationManagementDetailProps): React.ReactElement => {
  const resubmitEvent = useHttpClient(`${__PDND_API_URL__}/notifica/pratica-reinoltro/${id}`, {
    options: {
      method: 'POST',
    },
  });

  const formik = useFormik<NotificationManagementFormVM>({
    initialValues: {
      ...formData,
      stateItems,
    },
    validationSchema: yup.object().shape({
      recipient: yup
        .object()
        .shape({
          typedigitalDomicile: yup
            .string()
            .nullable()
            .when('digitalDomicile', {
              is: (value: string) => !!value,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string().notRequired(),
            }),
          digitalDomicile: yup.string().nullable().matches(regexEmail, 'Non è un formato email'),
        })
        .test(
          'recipient mandatory fields',
          'Entrambi i campi devono essere compilati oppure entrambi vuoti',
          (values) => {
            const castedValues = values as unknown as RecipientVM;
            const { typedigitalDomicile, digitalDomicile } = castedValues || {};
            const hasType = !!typedigitalDomicile;
            const hasValue = !!digitalDomicile;

            const keys = ['typedigitalDomicile', 'digitalDomicile'] as Array<keyof RecipientVM>;

            if ((hasType && hasValue) || (!hasType && !hasValue)) {
              return true;
            }
            const errors = keys
              .map((key) => {
                if (castedValues[key]) {
                  return;
                }
                return new yup.ValidationError(
                  'Campo obbligatorio',
                  castedValues[key],
                  `recipient.${key}`
                );
              })
              .filter((error) => !!error) as Array<yup.ValidationError>;
            if (errors.length === 0) {
              return true;
            }

            return new yup.ValidationError(errors);
          }
        ),
    }),
    onSubmit: (values) => {
      (
        resubmitEvent({
          data: {
            ...(values.item?.contenutoNotifica || {}),
            recipients: [
              {
                recipientType: values.recipient?.recipientType,
                taxId: values.recipient?.taxId,
                denomination: values.recipient?.denomination,
                digitalDomicile: {
                  type: values.recipient?.typedigitalDomicile,
                  address: values.recipient?.digitalDomicile,
                },
                physicalAddress: {
                  at: values.recipient?.at,
                  address: values.recipient?.address,
                  addressDetails: values.recipient?.addressDetails,
                  zip: values.recipient?.zip,
                  municipality: values.recipient?.municipality,
                  municipalityDetails: values.recipient?.municipalityDetails,
                  province: values.recipient?.province,
                  foreignState: values.recipient?.foreignState,
                },
                payment: values.recipient?.payment,
              },
            ],
          },
        }) as ResponsePromise<void>
      ).then(() => {
        onExit();
      });
    },
  });

  const disabled = useMemo(() => {
    return !(formik.values.state === 'ERROR' || formik.values.state === 'REFUSED');
  }, [formik.values.state]);

  const onChangeRecipent = useCallback(
    (field: keyof RecipientVM, value: RecipientVM[keyof RecipientVM]) => {
      formik.setFieldValue('recipient', {
        ...(formik.values.recipient || {}),
        [field]: value,
      });
    },
    [formik.values.recipient]
  );

  // eslint-disable-next-line no-console
  console.log(formik.errors, formik.touched);

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formik.handleSubmit}>
        <CardContent>
          <PmiTitle title="Dettaglio Notifica" paddingTop={0} />
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <PmiTextField
                label="ID Pratica"
                aria-label="ID Pratica"
                fullWidth
                onChange={noop}
                value={formik.values?.practiceId || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <PmiTextField
                label="Codice Comunicazione"
                aria-label="Codice Comunicazione"
                fullWidth
                onChange={noop}
                value={formik.values?.communicationId || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TypeCommunicationList
                onChange={noop}
                value={formik.values?.communicationType || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TypeTransactionList
                onChange={noop}
                value={formik.values?.transmissionType || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data trasmissione"
                aria-label="Data trasmissione"
                disabled
                value={formik.values?.trasmissionDate || null}
                onChange={noop}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="trasmissionDate"
                    fullWidth
                    error={
                      formik.touched?.trasmissionDate && Boolean(formik.errors?.trasmissionDate)
                    }
                    helperText={formik.errors?.trasmissionDate}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NotificationStateList value={formik.values.state || ''} onChange={noop} disabled />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data stato"
                aria-label="Data stato"
                disabled
                value={formik.values?.stateDate || null}
                onChange={noop}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="stateDate"
                    fullWidth
                    error={formik.touched?.stateDate && Boolean(formik.errors?.stateDate)}
                    helperText={formik.errors?.stateDate}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <PmiTitleDivider>Ricevente</PmiTitleDivider>
              <Grid item xs={12}>
                <PmiDivider />
              </Grid>
              <Grid item xs={12} container spacing={1}>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="denomination"
                    label="Denominazione"
                    aria-label="Denominazione"
                    fullWidth
                    disabled={disabled}
                    onChange={(event) => onChangeRecipent('denomination', event.target.value)}
                    value={formik.values?.recipient?.denomination || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TipoDomicilioDigitaleList
                    label="Tipo Domicilio Digitale"
                    aria-label="Tipo Domicilio Digitale"
                    disabled={disabled}
                    onChange={(event) =>
                      onChangeRecipent('typedigitalDomicile', event.target.value)
                    }
                    value={formik.values?.recipient?.typedigitalDomicile || ''}
                    error={
                      (formik.touched.recipient as unknown as FormikTouched<RecipientVM>)
                        ?.typedigitalDomicile &&
                      Boolean(
                        (formik.errors.recipient as unknown as FormikErrors<RecipientVM>)
                          ?.typedigitalDomicile
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="digitalDomicile"
                    label="Domicilio Digitale"
                    aria-label="Domicilio Digitale"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('digitalDomicile', event.target.value)}
                    value={formik.values?.recipient?.digitalDomicile || ''}
                    error={
                      (formik.touched.recipient as unknown as FormikTouched<RecipientVM>)
                        ?.digitalDomicile &&
                      Boolean(
                        (formik.errors.recipient as unknown as FormikErrors<RecipientVM>)
                          ?.digitalDomicile
                      )
                    }
                    helperText={
                      (formik.errors.recipient as unknown as FormikErrors<RecipientVM>)
                        ?.digitalDomicile
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="payment"
                    label="Pagamento"
                    aria-label="Pagamento"
                    disabled
                    fullWidth
                    onChange={(event) => onChangeRecipent('payment', event.target.value)}
                    value={formik.values?.recipient?.payment || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="recipientType"
                    label="Tipo Ricevente"
                    aria-label="Tipo Ricevente"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('recipientType', event.target.value)}
                    value={formik.values?.recipient?.recipientType || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="taxId"
                    label="Partita IVA"
                    aria-label="Partita IVA"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('taxId', event.target.value)}
                    value={formik.values?.recipient?.taxId || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PmiTitleDivider>Indirizzo</PmiTitleDivider>
                </Grid>
                <Grid item xs={12}>
                  <PmiDivider />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="address"
                    label="Indirizzo"
                    aria-label="Indirizzo"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('address', event.target.value)}
                    value={formik.values?.recipient?.address || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="at"
                    label="Presso"
                    aria-label="Presso"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('at', event.target.value)}
                    value={formik.values?.recipient?.at || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="foreignState"
                    label="Stato Estero"
                    aria-label="Stato Estero"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('foreignState', event.target.value)}
                    value={formik.values?.recipient?.foreignState || ''}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <PmiTextField
                    id="addressDetails"
                    label="Dettagli Indirizzo"
                    aria-label="Dettagli Indirizzo"
                    fullWidth
                    rows={5}
                    disabled={disabled}
                    multiline
                    onChange={(event) => onChangeRecipent('addressDetails', event.target.value)}
                    value={formik.values?.recipient?.addressDetails || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="municipality"
                    label="Comune"
                    aria-label="Comune"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('municipality', event.target.value)}
                    value={formik.values?.recipient?.municipality || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="province"
                    label="Provincia"
                    aria-label="Provincia"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('province', event.target.value)}
                    value={formik.values?.recipient?.province || ''}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiTextField
                    id="zip"
                    label="CAP"
                    aria-label="CAP"
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => onChangeRecipent('zip', event.target.value)}
                    value={formik.values?.recipient?.zip || ''}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <PmiTextField
                    id="municipalityDetails"
                    label="Dettagli Comune"
                    aria-label="Dettagli Comune"
                    fullWidth
                    rows={5}
                    disabled={disabled}
                    multiline
                    onChange={(event) =>
                      onChangeRecipent('municipalityDetails', event.target.value)
                    }
                    value={formik.values?.recipient?.municipalityDetails || ''}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <AccordionNotificationState
                ultimoScarico={ultimoScarico}
                notificationState={formik.values?.stateItems}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Reinoltra
          </PmiButton>
          <PmiButton variant="outlined" color="cdmBrand" type="button" onClick={() => onExit()}>
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

NotificationManagementDetailForm.displayName = 'Notification Management Detail Form';

export default NotificationManagementDetailForm;
